import { POS_OPTIONS } from "./pos"
import type { SenseEntry } from "./types"

// The one grouping rule shared by the preview template and the editor tree: senses grouped by part of
// speech, in a FIXED canonical order (noun, verb, adjective, … — the backend PARTS_OF_SPEECH order that
// CodexEntryPresenter sorts by). Sections aren't reorderable in the editor, so the order is derived, not
// authored. Used by sections (to rebuild the preview's CodexSectionView[]) and by the editor tree, so
// the editor, the preview, and a reload all group + order identically.

export interface SenseGroup {
  partOfSpeech: string
  senses: SenseEntry[]
}

/** The group key for a sense whose POS isn't set yet — pinned to the top ("Uncategorized"). */
export const UNCATEGORIZED = ""

// Canonical rank of a POS: unset/"Uncategorized" first, then PARTS_OF_SPEECH order, unknown last.
const posRank = (pos: string) => {
  if (pos === UNCATEGORIZED) return -1
  const i = POS_OPTIONS.indexOf(pos as (typeof POS_OPTIONS)[number])
  return i === -1 ? POS_OPTIONS.length : i
}

export const groupSensesByPos = (
  senses: SenseEntry[],
  // The group key for a sense. Defaults to its raw part of speech (the tree groups this way). The
  // preview overrides it to derive "proper_noun" for entity senses, mirroring CodexEntryPresenter.
  keyOf: (sense: SenseEntry) => string = (sense) => sense.partOfSpeech
): SenseGroup[] => {
  const byPos = new Map<string, SenseEntry[]>()
  for (const sense of senses) {
    const pos = keyOf(sense) || UNCATEGORIZED
    if (!byPos.has(pos)) byPos.set(pos, [])
    byPos.get(pos)!.push(sense)
  }
  return [...byPos.keys()]
    .sort((a, b) => posRank(a) - posRank(b))
    .map((pos) => ({ partOfSpeech: pos, senses: byPos.get(pos)! }))
}
